/**
 * /app/firm-access — Firm access (first-class tenancy, client side).
 *
 * Where a client (the trust-owner) sees and controls which firms can access
 * their data. It lives under the Security/Privacy area because the action is
 * access-control — delegating data access to a third party (the Google
 * "third-party access" / Xero "Advisors" pattern), not a profile attribute.
 *
 * Scenario 1: revoke a firm (fire your firm, keep your data). Revoke carries
 * real friction — a confirmation that spells out the consequence, because cutting
 * off the firm that administers your trusts is consequential. The destructive
 * confirm is the one place RED is correct (it encodes danger, not brand).
 *
 * All brand colour via tokens (var(--primary-*)/var(--accent-*) alias brand-primary/
 * accent); status/meaning colour (var(--negative) for the destructive action)
 * is fixed across skins.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ScreenShell } from '../../components/screens/screen-shell';
import { StateCard } from '../../components/state-card';
import { Icon } from '../../components/icon';
// A firm's avatar is its monogram, the one rule for a firm's mark.
import { monogramFor } from '../../components/firm-monogram';
import { Modal } from '../../components/ui/modal';
import { useToastStore } from '../../lib/toast-store';
import { mapError } from '../../lib/error-messages';
import { useTrusts } from '../../lib/api';
import {
  useGrants,
  useRevokeGrant,
  useGrantFirm,
  useSetGrantVisibility,
  grantVisibility,
  type FirmGrant,
} from '../../lib/api/grants';

export const Route = createFileRoute('/app/firm-access')({
  component: FirmAccessScreen,
});

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}


export function FirmAccessScreen() {
  const { data, isLoading, isError } = useGrants();
  const revoke = useRevokeGrant();
  const pushToast = useToastStore((s) => s.pushToast);
  const [revokeTarget, setRevokeTarget] = useState<FirmGrant | null>(null);
  // Lane B · B8: grant-a-firm form (Scenario 2 — one client, two firms).
  const [grantOpen, setGrantOpen] = useState(false);

  const grants = (data?.grants ?? []).filter((g) => g.status === 'active');

  function confirmRevoke() {
    if (!revokeTarget) return;
    const g = revokeTarget;
    revoke.mutate(
      { grantId: g.id },
      {
        onSuccess: () => {
          pushToast({ kind: 'success', message: `${g.firmDisplayName} no longer has access to your trusts.` });
          setRevokeTarget(null);
        },
        onError: (err) => {
          pushToast({ kind: 'error', message: mapError(err, 'revoke') });
          setRevokeTarget(null);
        },
      },
    );
  }

  return (
    <ScreenShell
      activeNav="firmAccess"
      eyebrow="Privacy & access"
      title="Firm access"
      sub="The firms that can see and manage your trusts. You're in control: grant or revoke at any time."
    >
      {/* Lane B · B8: grant a firm access (Scenario 2). Client-owner action —
          the backend gates to role 'user'/super_admin, which is exactly who
          reaches this screen. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button
          type="button"
          data-testid="grant-firm-open"
          className="btn-primary"
          onClick={() => setGrantOpen(true)}
          style={{ fontSize: 13.5 }}
        >
          Grant a firm access
        </button>
      </div>

      {isLoading ? (
        <StateCard
          variant="loading"
          testId="firm-access-loading"
          label="Firm access"
          headline="Reading which firms can see your trusts…"
        />
      ) : isError ? (
        <StateCard
          variant="error"
          testId="firm-access-error"
          label="Firm access"
          headline="Could not load firm access."
        />
      ) : grants.length === 0 ? (
        <StateCard
          variant="empty"
          testId="firm-access-empty"
          label="Firm access"
          headline="No firms have access to your trusts."
          detail="You're self-managing. Grant a firm access above to bring one on."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {grants.map((g) => {
            const isView = g.accessLevel === 'read';
            return (
              <div key={g.id} className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <span className="avatar brass" aria-hidden="true">{monogramFor(g.firmDisplayName) || 'F'}</span>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{g.firmDisplayName}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>Granted {fmtDate(g.createdAt)}</div>
                  </div>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '3px 9px',
                      borderRadius: 999,
                      background: isView ? 'var(--surface-2)' : 'var(--primary-100)',
                      color: isView ? 'var(--ink-2)' : 'var(--primary-700)',
                    }}
                  >
                    {isView ? <Icon name="lock" size={12} /> : null}
                    {isView ? 'View only' : 'Can manage'}
                  </span>
                  <button type="button" className="btn-secondary" onClick={() => setRevokeTarget(g)} style={{ fontSize: 13 }}>
                    Revoke
                  </button>
                </div>
                <FirmVisibilityControls grant={g} />
              </div>
            );
          })}
        </div>
      )}

      <Modal open={revokeTarget !== null} onClose={() => setRevokeTarget(null)} title="Revoke firm access">
        <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 16px' }}>
          <strong style={{ color: 'var(--ink)' }}>{revokeTarget?.firmDisplayName}</strong> will{' '}
          <strong>immediately</strong> lose access to your trusts and related documents. You&rsquo;ll need to appoint
          another firm or manage your trusts yourself. Your data stays with you.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={() => setRevokeTarget(null)} disabled={revoke.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={confirmRevoke}
            disabled={revoke.isPending}
            style={{ background: 'var(--negative)', borderColor: 'var(--negative)' }}
          >
            {revoke.isPending ? 'Revoking…' : 'Revoke access'}
          </button>
        </div>
      </Modal>

      <GrantFirmModal
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        onGranted={(name) =>
          pushToast({ kind: 'success', message: `${name} now has access to your trusts.` })
        }
      />
    </ScreenShell>
  );
}

// ── Per-firm VISIBILITY SCOPES (Paul + Beno, 2026-08-23) ────────────────────
//
// THE RULING, in Paul's words: the client must be able to "click on the company
// and decide what they can see and what they can manage — total net worth,
// asset lists — tick what they can see and can't see." Beno's half: a firm
// should "only be able to view information directly relevant to the specific
// trusts they manage, rather than the client's total private asset portfolio".
//
// So: two ticks per firm, and they govern the ESTATE only. The trusts the firm
// administers stay fully visible either way — a firm that cannot see the trust
// it runs cannot run it, and that is not what either man asked for. The copy
// below says that out loud, because a privacy control the user misreads is
// worse than none.
//
// These are NOT cosmetic. Unticking writes through to the authoritative grant
// and every firm-facing estate read re-reads it on the very next request. The
// UI renders the SERVER's answer, never an optimistic local one — a checkbox
// that flips before the write lands is a lie about a permission.
function FirmVisibilityControls({ grant }: { grant: FirmGrant }) {
  const setVisibility = useSetGrantVisibility();
  const pushToast = useToastStore((s) => s.pushToast);
  // PRV-02 — SAY WHICH TRUSTS, BECAUSE THE TICKS DO NOT DECIDE THAT.
  //
  // Beno's half of the ruling was per-TRUST: "Destinata should see only the
  // trusts it administers, never his total private portfolio." Only the second
  // clause is built. A grant is issued at CLIENT level, so a firm with a grant
  // reaches every trust in this workspace, and the two ticks below govern the
  // estate aggregates alone. Read quickly, "what this firm can see" plus two
  // ticks reads as if it were the whole answer, which would leave a client
  // believing they had narrowed something they had not.
  //
  // Per-trust scoping is NOT faked here with a third toggle. It is a backend
  // change and it is named in the handoff: the persisted shape already carries
  // `scope`/`trustIds` (repositories/types.ts), and `firm-grant-service.ts`
  // REFUSES to write `scope: 'specific-trusts'` on purpose, because the act-as
  // token carries a whole-client role with no per-trust filter, so persisting
  // one would silently grant every trust anyway.
  const trustsQ = useTrusts();
  const trustCount = trustsQ.data?.trusts.length ?? 0;
  // Which key is mid-flight — so only the toggled row disables, not both.
  const [pending, setPending] = useState<'netWorth' | 'assetList' | null>(null);

  function toggle(key: 'netWorth' | 'assetList', next: boolean) {
    if (setVisibility.isPending) return;
    setPending(key);
    setVisibility.mutate(
      { grantId: grant.id, [key]: next },
      {
        onSuccess: () => setPending(null),
        onError: (err) => {
          setPending(null);
          pushToast({ kind: 'error', message: mapError(err, 'save') });
        },
      },
    );
  }

  // Read through the resolver, never `grant.visibility` directly — an absent
  // field (pre-2026-08-23 backend, mixed deploy window) means "can see both".
  const visibility = grantVisibility(grant);
  const rows = [
    {
      key: 'netWorth' as const,
      label: 'Can see my total portfolio value',
      hint: 'Your estate headline and the personal/trust split. Off, they see only the trusts they administer.',
      checked: visibility.netWorth,
    },
    {
      key: 'assetList' as const,
      label: 'Can see my full asset list',
      hint: 'Assets you hold personally, outside any trust. Assets inside the trusts they administer stay visible.',
      checked: visibility.assetList,
    },
  ];

  return (
    <div
      data-testid={`firm-visibility-${grant.id}`}
      style={{
        marginTop: 14,
        paddingTop: 12,
        borderTop: '1px solid var(--hairline)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600, letterSpacing: '0.02em' }}>
        WHAT THIS FIRM CAN SEE
      </div>
      {trustCount > 0 ? (
        <div
          data-testid={`firm-trust-scope-${grant.id}`}
          style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55 }}
        >
          {grant.firmDisplayName} can open{' '}
          <strong style={{ color: 'var(--ink-2)' }}>
            all {trustCount} {trustCount === 1 ? 'trust' : 'trusts'}
          </strong>{' '}
          in this workspace. The ticks below govern what they see of your
          personal estate, not which trusts they reach.
        </div>
      ) : null}
      {rows.map((r) => (
        <label
          key={r.key}
          style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            data-testid={`firm-visibility-${r.key}-${grant.id}`}
            checked={r.checked}
            disabled={pending !== null}
            onChange={(e) => toggle(r.key, e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            <span style={{ display: 'block', fontSize: 13.5, color: 'var(--ink)' }}>{r.label}</span>
            <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.5 }}>
              {r.hint}
            </span>
          </span>
        </label>
      ))}
      {/* HONESTY NOTE. The two ticks are independent and CAN contradict each
          other: hand a firm every asset and its value, and they can add the
          list up whether or not the total is ticked off. We enforce exactly
          what each tick says and tell the client the rest, rather than quietly
          overriding a choice they made. */}
      {!visibility.netWorth && visibility.assetList ? (
        <div
          data-testid={`firm-visibility-note-${grant.id}`}
          style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5, marginTop: 2 }}
        >
          Note: they can still see each personal asset and its value, so they could add
          them up. Switch off the asset list too if you&rsquo;d rather they couldn&rsquo;t.
        </div>
      ) : null}
    </div>
  );
}

// ── Lane B · B8: grant-a-firm modal (Scenario 2 — one client, two firms) ─────
//
// POST /v1/grants via the existing `useGrantFirm` (previously never called).
// MVP is MANUAL identifier entry — there is no public firm directory, so the
// firm shares its Elreign tenant identifier with the client out-of-band (the
// same way you'd share a banking reference). The backend validates the target
// is a REAL b2b firm (400 INVALID_FIRM) and refuses self-grants
// (CANNOT_GRANT_SELF); access level defaults to full management, mirroring
// the appoint-your-firm reality.
function GrantFirmModal({
  open,
  onClose,
  onGranted,
}: {
  open: boolean;
  onClose: () => void;
  onGranted: (firmIdentifier: string) => void;
}) {
  const grant = useGrantFirm();
  const [firmTenantId, setFirmTenantId] = useState('');
  const [accessLevel, setAccessLevel] = useState<'read' | 'manage'>('manage');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function reset() {
    setFirmTenantId('');
    setAccessLevel('manage');
    setErrorMsg(null);
  }

  function handleClose() {
    if (grant.isPending) return;
    reset();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (grant.isPending) return;
    setErrorMsg(null);
    const id = firmTenantId.trim();
    if (!id) {
      setErrorMsg('Enter the firm identifier your firm shared with you.');
      return;
    }
    grant.mutate(
      { firmTenantId: id, accessLevel },
      {
        onSuccess: () => {
          onGranted(id);
          reset();
          onClose();
        },
        onError: (err) => {
          const code = (err as { code?: string }).code;
          setErrorMsg(
            code === 'INVALID_FIRM'
              ? 'That identifier doesn’t match a registered firm on Elreign. Check it with your firm and try again.'
              : code === 'CANNOT_GRANT_SELF'
                ? 'That identifier is your own account, not a firm’s.'
                : mapError(err, 'save'),
          );
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={handleClose} title="Grant a firm access">
      <form onSubmit={handleSubmit}>
        <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 16px' }}>
          The firm will see your trusts and, with management access, act on
          them on your behalf. You stay in control: revoke at any time from
          this screen, and everything a firm does is recorded on your audit
          trail.
        </p>

        <label
          htmlFor="grant-firm-id"
          style={{ fontSize: 12.5, color: 'var(--ink-3)', display: 'block', marginBottom: 6 }}
        >
          Firm identifier
        </label>
        <input
          id="grant-firm-id"
          data-testid="grant-firm-id"
          type="text"
          value={firmTenantId}
          disabled={grant.isPending}
          onChange={(e) => setFirmTenantId(e.target.value)}
          placeholder="The Elreign identifier your firm shared with you"
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1px solid var(--hairline-2)', background: 'var(--bg)',
            color: 'var(--ink)', fontFamily: 'var(--sans)', fontSize: 14,
            boxSizing: 'border-box', marginBottom: 14,
          }}
        />

        <div role="radiogroup" aria-label="Access level" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {([
            { v: 'manage' as const, label: 'Can manage', hint: 'See and administer your trusts: the normal arrangement with your appointed firm.' },
            { v: 'read' as const, label: 'View only', hint: 'See your trusts but change nothing, e.g. a second firm giving an opinion.' },
          ]).map((o) => (
            <label
              key={o.v}
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px',
                border: `1px solid ${accessLevel === o.v ? 'var(--primary-700)' : 'var(--hairline)'}`,
                borderRadius: 10, cursor: 'pointer', background: accessLevel === o.v ? 'var(--primary-50, var(--surface-2))' : 'var(--surface)',
              }}
            >
              <input
                type="radio"
                name="grant-access-level"
                data-testid={`grant-level-${o.v}`}
                checked={accessLevel === o.v}
                disabled={grant.isPending}
                onChange={() => setAccessLevel(o.v)}
                style={{ marginTop: 2 }}
              />
              <span>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{o.label}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.5 }}>{o.hint}</span>
              </span>
            </label>
          ))}
        </div>

        {errorMsg && (
          <div
            role="alert"
            data-testid="grant-firm-error"
            style={{
              fontSize: 13, color: 'var(--negative)', background: 'var(--negative-soft)',
              padding: '10px 12px', borderRadius: 8, marginBottom: 14,
            }}
          >
            {errorMsg}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={grant.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            data-testid="grant-firm-submit"
            disabled={grant.isPending}
          >
            {grant.isPending ? 'Granting…' : 'Grant access'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
